import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    FaArrowLeft, FaMagic, FaCheck, FaTrash, FaUpload, FaUsers, FaImage,
    FaPlus, FaArrowUp, FaArrowDown, FaPaperclip, FaSave, FaFileUpload, FaVideo, FaPen,
} from "react-icons/fa";
import { authFetch } from "../../utils/api/authFetch";
import { useTeacherTaxonomy } from "../../hooks/useTeacherTaxonomy";
import { MathText, MathJaxContext, mathJaxConfig } from "../../components/MathText";
import { formatAiText } from "../../utils/formatAiText";
import {
    ContentThumb, TopicSelect, SubjectPicker, ClassPills, TypePicker, ProfileSetupNotice,
} from "../../components/marketplace";
import { SecureMedia } from "../../components/protected";

// Friendly, student-neutral labels for question types in the review UI.
const QTYPE_LABELS = { mcq: "Multiple choice", theory: "Theory" };

// A teacher can upload whole lessons as files — each one becomes its own
// segment. Documents AND videos go through here; the per-segment "Attach file"
// control accepts the same set plus images.
const BULK_ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.mp4,.mov,.webm,.m4v";
const SEGMENT_ACCEPT = `${BULK_ACCEPT},.jpg,.jpeg,.png`;

// Videos are held to a 50MB cap (enforced again server-side). We check on the
// client too so a teacher gets an instant, friendly rejection instead of waiting
// for a large upload to fail. Matches VIDEO_EXTENSIONS in marketplaceUpload.js.
const VIDEO_MAX_BYTES = 50 * 1024 * 1024;
const isVideoFile = (file) => !!file && /\.(mp4|mov|webm|m4v)$/i.test(file.name);

// Gate a chosen segment file through the video size cap. Returns the file when
// it's acceptable, or null (after toasting) so callers can reset the input.
const acceptSegmentFile = (file) => {
    if (file && isVideoFile(file) && file.size > VIDEO_MAX_BYTES) {
        toast.error(`That video is ${Math.round(file.size / (1024 * 1024))}MB — videos must be 50MB or smaller.`);
        return null;
    }
    return file || null;
};

const ContentBuilder = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lesson, setLesson] = useState(null);
    const [segments, setSegments] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [students, setStudents] = useState(null);
    const [genOptions, setGenOptions] = useState({ mcqCount: 8, theoryCount: 3, flashcardCount: 8 });

    // Subject/class options come from the teacher's own profile; topics stay
    // global-per-subject (shared across classes).
    const { subjects, classes, topicsBySubject, hasSubjects, hasClasses, needsSetup } = useTeacherTaxonomy();

    // Which pending questions are checked to keep. After a generation we pre-check
    // exactly the requested number of MCQ/theory (extras the AI over-produced
    // start unchecked); `askRef` carries that requested count into the reseed
    // effect, then resets so later refetches just keep everything selected.
    const [selectedQ, setSelectedQ] = useState(() => new Set());
    const [reviewBusy, setReviewBusy] = useState(false);
    const askRef = useRef({ mcq: null, theory: null });

    // Item settings form (mirrors the loaded lesson).
    const [settings, setSettings] = useState(null);
    const [cover, setCover] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [uploadingDocs, setUploadingDocs] = useState(false);

    useEffect(() => {
        if (!cover) { setCoverPreview(null); return; }
        const url = URL.createObjectURL(cover);
        setCoverPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [cover]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const lRes = await authFetch(`/api/lessons/mine/${id}`);
            const data = await lRes.json().catch(() => ({}));
            if (!lRes.ok) throw new Error(data.message || "Failed to load content");
            const l = data.data.lesson;
            setLesson(l);
            setSegments(data.data.segments || []);
            setQuestions(data.data.questions || []);
            setFlashcards(data.data.flashcards || []);
            setSettings({
                title: l.title || "",
                subject: l.subject || "",
                class: l.class || "",
                topic: l.topic || "",
                type: l.type || "lesson_notes",
                description: l.description || "",
                body: l.body || "",
            });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, [id]);

    // Re-seed the "keep" selection whenever the pending-draft set changes. Right
    // after a generation `askRef` holds the requested counts, so we pre-check the
    // first N of each type; otherwise every remaining draft stays checked.
    useEffect(() => {
        const drafts = questions.filter((q) => q.review_status === "draft");
        if (!drafts.length) { setSelectedQ(new Set()); return; }
        const pick = (type, n) => {
            const ids = drafts.filter((q) => q.question_type === type).map((q) => q.id);
            return n == null ? ids : ids.slice(0, n);
        };
        setSelectedQ(new Set([...pick("mcq", askRef.current.mcq), ...pick("theory", askRef.current.theory)]));
        askRef.current = { mcq: null, theory: null };
    }, [questions]);

    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            const body = new FormData();
            Object.entries(settings).forEach(([k, v]) => body.append(k, v ?? ""));
            if (cover) body.append("thumbnail", cover);
            const res = await authFetch(`/api/lessons/${id}`, { method: "PATCH", body });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Couldn't save");
            toast.success("Content details saved");
            setCover(null);
            fetchAll();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSavingSettings(false);
        }
    };

    const handleGenerate = async () => {
        const hasDrafts = questions.some((q) => q.review_status === "draft") || flashcards.some((f) => f.status === "draft");
        if (hasDrafts && !window.confirm("This replaces your current pending drafts (anything you already approved is kept). Generate again?")) return;
        // Remember what was asked for so the reseed effect pre-checks exactly that many.
        askRef.current = {
            mcq: Math.max(parseInt(genOptions.mcqCount, 10) || 0, 0),
            theory: Math.max(parseInt(genOptions.theoryCount, 10) || 0, 0),
        };
        setGenerating(true);
        try {
            const res = await authFetch(`/api/lessons/${id}/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(genOptions),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Generation failed");
            toast.success("Draft content generated — review and keep the ones you want");
            fetchAll();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const toggleQ = (qid) => setSelectedQ((prev) => {
        const next = new Set(prev);
        if (next.has(qid)) next.delete(qid); else next.add(qid);
        return next;
    });
    const setAllQ = (ids, on) => setSelectedQ(on ? new Set(ids) : new Set());

    // Keep the ticked questions (bulk approve); the unticked ones stay as drafts
    // until you discard them.
    const approveSelected = async (draftIds) => {
        const ids = draftIds.filter((qid) => selectedQ.has(qid));
        if (!ids.length) { toast.error("Tick at least one question to keep"); return; }
        setReviewBusy(true);
        try {
            await Promise.all(ids.map((qid) => authFetch(`/api/teacher-questions/${qid}/approve`, { method: "PATCH" })));
            toast.success(`Kept ${ids.length} question${ids.length === 1 ? "" : "s"}`);
            fetchAll();
        } catch {
            toast.error("Couldn't approve some questions");
        } finally {
            setReviewBusy(false);
        }
    };

    // Drop the unticked (extra) questions in one go.
    const discardUnselected = async (draftIds) => {
        const ids = draftIds.filter((qid) => !selectedQ.has(qid));
        if (!ids.length) { toast.error("Nothing unchecked to discard"); return; }
        if (!window.confirm(`Discard ${ids.length} unchecked question${ids.length === 1 ? "" : "s"}?`)) return;
        setReviewBusy(true);
        try {
            await Promise.all(ids.map((qid) => authFetch(`/api/teacher-questions/${qid}`, { method: "DELETE" })));
            toast.success("Removed the unchecked questions");
            fetchAll();
        } catch {
            toast.error("Couldn't discard some questions");
        } finally {
            setReviewBusy(false);
        }
    };

    const approveFlashcard = async (fid) => { await authFetch(`/api/lessons/flashcards/${fid}/approve`, { method: "PATCH" }); fetchAll(); };
    const discardFlashcard = async (fid) => { await authFetch(`/api/lessons/flashcards/${fid}/discard`, { method: "PATCH" }); fetchAll(); };

    // Add a question BY HAND (the only path for video lessons, where AI is off).
    // The lesson supplies subject/class/topic server-side; it lands approved.
    const addQuestion = async (payload) => {
        const res = await authFetch(`/api/lessons/${id}/questions`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't add the question");
        toast.success("Question added");
        fetchAll();
    };

    const handlePublish = async () => {
        try {
            const res = await authFetch(`/api/lessons/${id}/publish`, { method: "POST" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Failed to publish");
            toast.success("Published — your students can see it now");
            fetchAll();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const loadStudents = async () => {
        try {
            const res = await authFetch(`/api/lessons/${id}/students`);
            const data = await res.json().catch(() => ({}));
            setStudents(data.data || []);
        } catch (err) {
            toast.error("Could not load student progress");
        }
    };

    // ---- Segments ----
    const addSegment = async ({ title, body, topic, file }) => {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("body", body || "");
        fd.append("topic", topic || "");
        if (file) fd.append("attachment", file);
        const res = await authFetch(`/api/lessons/${id}/segments`, { method: "POST", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't add segment");
        fetchAll();
    };
    const saveSegment = async (sid, { title, body, topic, file, removeAttachment }) => {
        const fd = new FormData();
        fd.append("title", title);
        fd.append("body", body || "");
        fd.append("topic", topic || "");
        if (file) fd.append("attachment", file);
        if (removeAttachment) fd.append("remove_attachment", "true");
        const res = await authFetch(`/api/lessons/segments/${sid}`, { method: "PATCH", body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Couldn't save segment");
        fetchAll();
    };
    const deleteSegment = async (sid) => {
        if (!window.confirm("Delete this segment?")) return;
        const res = await authFetch(`/api/lessons/segments/${sid}`, { method: "DELETE" });
        if (!res.ok) { toast.error("Couldn't delete segment"); return; }
        fetchAll();
    };
    const moveSegment = async (index, dir) => {
        const target = index + dir;
        if (target < 0 || target >= segments.length) return;
        const order = segments.map((s) => s.id);
        [order[index], order[target]] = [order[target], order[index]];
        const res = await authFetch(`/api/lessons/${id}/segments/reorder`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order }),
        });
        if (!res.ok) { toast.error("Couldn't reorder"); return; }
        fetchAll();
    };

    // Upload one or more files at once — each becomes its own segment (title =
    // the file name). The fast path for teachers who already have their lessons
    // as PDFs, Word docs or recorded clips instead of typing them out. Reuses
    // the same createSegment endpoint the "Attach file" control uses; students
    // then read/watch every one of them inside the app.
    const uploadDocuments = async (fileList) => {
        const chosen = Array.from(fileList || []);
        if (!chosen.length) return;
        // Drop oversized videos up front (acceptSegmentFile toasts the reason)
        // so one 300MB clip doesn't sink the whole batch.
        const files = chosen.filter((f) => acceptSegmentFile(f));
        if (!files.length) return;

        setUploadingDocs(true);
        let ok = 0;
        try {
            for (const file of files) {
                const fd = new FormData();
                fd.append("title", file.name.replace(/\.[^.]+$/, "") || "Document");
                fd.append("attachment", file);
                const res = await authFetch(`/api/lessons/${id}/segments`, { method: "POST", body: fd });
                if (res.ok) ok += 1;
            }
            if (ok) toast.success(`Added ${ok} file${ok === 1 ? "" : "s"} as segment${ok === 1 ? "" : "s"}`);
            if (ok < files.length) toast.error(`${files.length - ok} file(s) couldn't be uploaded — check the type/size.`);
            fetchAll();
        } catch (err) {
            toast.error("Upload failed");
        } finally {
            setUploadingDocs(false);
        }
    };

    if (loading) return <div className="p-10 text-gray-500">Loading…</div>;
    if (!lesson || !settings) return <div className="p-10 text-gray-500">Content not found.</div>;

    const draftQuestions = questions.filter((q) => q.review_status === "draft");
    const approvedQuestions = questions.filter((q) => q.review_status === "approved");
    const draftFlashcards = flashcards.filter((f) => f.status === "draft");
    // Video lessons can't be summarised/quizzed by the AI (it can't watch the
    // clip), so those lessons are manual-questions-only — the AI panel + its
    // draft/summary/flashcard review are hidden and a hand-write form is shown.
    const isVideo = settings.type === "video";

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="min-h-screen bg-gray-50 font-montserrat">
                <nav className="bg-[#2148C0] shadow-md px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <button onClick={() => navigate("/teacher/lessons")} className="text-white shrink-0"><FaArrowLeft size={18} /></button>
                        <h1 className="text-lg sm:text-xl font-bold text-white truncate">{lesson.title}</h1>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <button onClick={loadStudents} className="text-white flex items-center gap-2 text-sm"><FaUsers /> <span className="hidden sm:inline">Progress</span></button>
                        {lesson.status !== "published" && (
                            <button onClick={handlePublish} className="bg-white text-blue-700 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 text-sm">
                                <FaUpload /> Publish
                            </button>
                        )}
                    </div>
                </nav>

                <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
                    {lesson.status === "published" && (
                        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3">
                            Published — visible to your linked students. To sell it, list it on the Marketplace page.
                        </div>
                    )}

                    {students && (
                        <div className="bg-white rounded-xl shadow-sm p-4">
                            <h3 className="font-semibold mb-2">Student Progress</h3>
                            {students.length === 0 && <p className="text-sm text-gray-500">No student has opened this yet.</p>}
                            {students.map((s) => (
                                <div key={s.student_id} className="flex justify-between text-sm py-1 border-t first:border-t-0">
                                    <span>{s.name}</span>
                                    <span className="text-gray-500">{s.completed_at ? `Completed · ${s.practice_score ?? "-"}%` : "In progress"}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Item settings */}
                    <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-6 space-y-5">
                        <h3 className="font-semibold text-gray-800">Details</h3>
                        {needsSetup && (
                            <ProfileSetupNotice
                                missingSubjects={!hasSubjects}
                                missingClasses={!hasClasses}
                                onSetup={() => navigate("/teacher/marketplace/profile")}
                            />
                        )}
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                            <input placeholder="Lesson title" className="border border-gray-200 rounded-xl p-2.5 w-full focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none" value={settings.title}
                                onChange={(e) => setSettings({ ...settings, title: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <SubjectPicker label="Subject" allowCustom={false} subjects={subjects} value={settings.subject}
                                onChange={(v) => setSettings({ ...settings, subject: v })} />
                            <ClassPills label="Class" classes={classes} value={settings.class}
                                onChange={(v) => setSettings({ ...settings, class: v })} />
                        </div>
                        <TopicSelect label="Topic (optional)" hint="From your question bank for this subject — the same topics apply across every class."
                            subject={settings.subject} topicsBySubject={topicsBySubject}
                            value={settings.topic} onChange={(v) => setSettings({ ...settings, topic: v })} />
                        <TypePicker label="Content type" value={settings.type} onChange={(v) => setSettings({ ...settings, type: v })} />
                        <textarea placeholder="Short description (shown in the marketplace feed)" rows={2} className="border rounded-lg p-2 w-full" value={settings.description}
                            onChange={(e) => setSettings({ ...settings, description: e.target.value })} />
                        <textarea placeholder="Intro / overview (optional — the detail goes in segments below)" rows={3} className="border rounded-lg p-2 w-full" value={settings.body}
                            onChange={(e) => setSettings({ ...settings, body: e.target.value })} />

                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="w-full sm:w-48 shrink-0">
                                <p className="text-xs font-medium text-gray-500 mb-1">Cover preview</p>
                                {coverPreview ? (
                                    <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 border"><img src={coverPreview} alt="cover" className="w-full h-full object-cover" /></div>
                                ) : (
                                    <ContentThumb type={settings.type} thumbnailUrl={lesson.thumbnail_url} title={settings.title || "Your content"} />
                                )}
                                <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer border rounded-lg px-3 py-2">
                                    <FaImage className="shrink-0" /> <span className="truncate">{cover ? cover.name : "Change cover (JPG/PNG)"}</span>
                                    <input type="file" accept=".jpg,.jpeg,.png" className="hidden" onChange={(e) => setCover(e.target.files[0])} />
                                </label>
                            </div>
                        </div>

                        <button onClick={saveSettings} disabled={savingSettings} className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50">
                            {savingSettings ? "Saving…" : "Save details"}
                        </button>
                    </div>

                    {/* Segments */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <h3 className="font-semibold text-gray-800">Segments ({segments.length})</h3>
                            <label className={`flex items-center gap-2 text-sm font-semibold cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-2 rounded-lg shrink-0 ${uploadingDocs ? "opacity-50 pointer-events-none" : ""}`}>
                                <FaFileUpload className="shrink-0" /> {uploadingDocs ? "Uploading…" : "Upload PDFs / docs / videos"}
                                <input type="file" accept={BULK_ACCEPT} multiple className="hidden"
                                    onChange={(e) => { uploadDocuments(e.target.files); e.target.value = ""; }} />
                            </label>
                        </div>
                        <p className="text-xs text-gray-500 -mt-2">
                            Break the lesson into smaller parts — type text, or upload PDFs, Word docs and videos (each file becomes a segment; videos max 50MB).
                            Students read and watch everything inside Teechaa: no downloads, no copying.
                        </p>

                        {segments.map((seg, idx) => (
                            <SegmentCard
                                key={seg.id}
                                segment={seg}
                                index={idx}
                                total={segments.length}
                                subject={settings.subject}
                                topicsBySubject={topicsBySubject}
                                onSave={(patch) => saveSegment(seg.id, patch)}
                                onDelete={() => deleteSegment(seg.id)}
                                onMove={(dir) => moveSegment(idx, dir)}
                            />
                        ))}

                        <AddSegment subject={settings.subject} topicsBySubject={topicsBySubject} onAdd={addSegment} />
                    </div>

                    {/* AI generation — hidden for video lessons (the AI can't watch a clip) */}
                    {isVideo ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 flex items-start gap-3">
                            <span className="w-9 h-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><FaVideo size={15} /></span>
                            <div className="text-sm">
                                <p className="font-semibold text-rose-800">Video lesson — questions are added by hand</p>
                                <p className="text-rose-700 mt-0.5">AI can't watch a video, so it won't generate questions here. Upload your clip as a segment above (max 50MB), then write the practice questions yourself below.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><FaMagic /> Generate with AI</h3>
                            <p className="text-xs text-gray-500 mb-3">Uses your intro + all segment text to draft practice questions, a summary and flashcards.</p>
                            <div className="grid grid-cols-3 gap-4 mb-4">
                                {[["mcqCount", "Multiple choice", 20], ["theoryCount", "Theory", 10], ["flashcardCount", "Flashcards", 20]].map(([key, label, max]) => (
                                    <label key={key} className="text-sm text-gray-600">{label}
                                        <input type="number" min={0} max={max} className="border rounded-lg p-2 w-full mt-1" value={genOptions[key]}
                                            onChange={(e) => setGenOptions({ ...genOptions, [key]: e.target.value })} />
                                    </label>
                                ))}
                            </div>
                            <button disabled={generating} onClick={handleGenerate} className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50">
                                {generating ? "Generating…" : "Generate practice, summary & flashcards"}
                            </button>
                        </div>
                    )}

                    {/* Add a question by hand — the only path for video lessons, and a
                        handy complement to AI for the others. */}
                    <AddQuestion onAdd={addQuestion} />

                    {!isVideo && lesson.ai_summary && (
                        <div className="bg-blue-50 rounded-xl p-6">
                            <h3 className="font-semibold text-blue-800 mb-2">AI Summary</h3>
                            <p className="text-sm text-blue-900 whitespace-pre-wrap">{formatAiText(lesson.ai_summary)}</p>
                        </div>
                    )}

                    {!isVideo && draftQuestions.length > 0 && (() => {
                        const draftIds = draftQuestions.map((q) => q.id);
                        const selCount = draftIds.filter((qid) => selectedQ.has(qid)).length;
                        const allOn = selCount === draftIds.length;
                        return (
                            <div>
                                <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                                    <h3 className="font-semibold text-gray-800">
                                        Review questions <span className="text-gray-400 font-normal">· {selCount} of {draftQuestions.length} kept</span>
                                    </h3>
                                    <button onClick={() => setAllQ(draftIds, !allOn)} className="text-xs font-semibold text-blue-600 hover:underline">
                                        {allOn ? "Clear all" : "Select all"}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mb-3">The AI can return a few more than you asked for — the number you requested is pre-ticked. Adjust, then keep the ones you want.</p>

                                <div className="space-y-2.5">
                                    {draftQuestions.map((q) => {
                                        const on = selectedQ.has(q.id);
                                        return (
                                            <div key={q.id} className={`bg-white rounded-xl shadow-sm p-4 border-2 transition ${on ? "border-blue-400" : "border-transparent opacity-80"}`}>
                                                <label className="flex items-start gap-3 cursor-pointer">
                                                    <input type="checkbox" checked={on} onChange={() => toggleQ(q.id)} className="mt-1 h-4 w-4 accent-blue-600 shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-[11px] tracking-wide font-semibold text-gray-400">{QTYPE_LABELS[q.question_type] || q.question_type}</span>
                                                        <MathText text={q.question} className="block font-medium text-gray-800" />
                                                        {q.question_type === "mcq" && (
                                                            <ul className="mt-1 text-sm text-gray-600">
                                                                {["A", "B", "C", "D"].map((l) => (
                                                                    <li key={l} className={q.correctAnswer === l ? "text-green-600 font-semibold" : ""}>
                                                                        {l}. <MathText text={q[`option_${l.toLowerCase()}`]} />
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {q.question_type === "theory" && q.explanation && (
                                                            <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">Model answer: {formatAiText(q.explanation)}</p>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="flex flex-wrap items-center gap-3 mt-4">
                                    <button onClick={() => approveSelected(draftIds)} disabled={reviewBusy || selCount === 0}
                                        className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50">
                                        <FaCheck /> {reviewBusy ? "Saving…" : `Keep selected (${selCount})`}
                                    </button>
                                    <button onClick={() => discardUnselected(draftIds)} disabled={reviewBusy || allOn}
                                        className="text-red-600 border border-red-200 hover:bg-red-50 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-40">
                                        <FaTrash /> Discard unchecked
                                    </button>
                                </div>
                            </div>
                        );
                    })()}

                    {!isVideo && draftFlashcards.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Pending Flashcards ({draftFlashcards.length})</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {draftFlashcards.map((f) => (
                                    <div key={f.id} className="bg-white rounded-xl shadow-sm p-4">
                                        <p className="font-medium text-gray-800 text-sm">{f.front}</p>
                                        <p className="text-sm text-gray-500 mt-1">{f.back}</p>
                                        <div className="flex gap-2 mt-2">
                                            <button onClick={() => approveFlashcard(f.id)} className="text-green-600 hover:bg-green-50 p-2 rounded-lg"><FaCheck /></button>
                                            <button onClick={() => discardFlashcard(f.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><FaTrash /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 className="font-semibold text-gray-800 mb-3">Approved Practice ({approvedQuestions.length})</h3>
                        {approvedQuestions.length === 0 && <p className="text-sm text-gray-500">Nothing approved yet.</p>}
                        <div className="space-y-2">
                            {approvedQuestions.map((q) => (
                                <div key={q.id} className="bg-white rounded-xl shadow-sm p-3">
                                    <MathText text={q.question} className="text-sm text-gray-700" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </MathJaxContext>
    );
};

// One editable segment card. Keeps a local draft so typing doesn't refetch;
// "Save" persists via PATCH. Shows/replaces/removes the single attachment.
const SegmentCard = ({ segment, index, total, subject, topicsBySubject, onSave, onDelete, onMove }) => {
    const [title, setTitle] = useState(segment.title || "");
    const [body, setBody] = useState(segment.body || "");
    const [topic, setTopic] = useState(segment.topic || "");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(false);

    const save = async (extra = {}) => {
        if (!title.trim()) { toast.error("Segment title is required"); return; }
        setSaving(true);
        try {
            await onSave({ title, body, topic, file, ...extra });
            setFile(null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-400 w-6 shrink-0">#{index + 1}</span>
                <input placeholder="Segment title" className="border rounded-lg p-2 flex-1" value={title} onChange={(e) => setTitle(e.target.value)} />
                <div className="flex items-center gap-0.5 shrink-0">
                    <button onClick={() => onMove(-1)} disabled={index === 0} className="text-gray-400 hover:text-blue-600 p-1.5 disabled:opacity-30" title="Move up"><FaArrowUp size={13} /></button>
                    <button onClick={() => onMove(1)} disabled={index === total - 1} className="text-gray-400 hover:text-blue-600 p-1.5 disabled:opacity-30" title="Move down"><FaArrowDown size={13} /></button>
                    <button onClick={onDelete} className="text-gray-400 hover:text-red-500 p-1.5" title="Delete"><FaTrash size={13} /></button>
                </div>
            </div>
            <textarea placeholder="Segment text (optional)" rows={4} className="border rounded-lg p-2 w-full text-sm" value={body} onChange={(e) => setBody(e.target.value)} />
            <TopicSelect subject={subject} topicsBySubject={topicsBySubject} value={topic} onChange={setTopic} placeholder="Segment topic (optional)" />

            <div className="flex flex-wrap items-center gap-3 text-sm">
                <label className="flex items-center gap-2 text-gray-600 cursor-pointer border rounded-lg px-3 py-2">
                    <FaPaperclip className="shrink-0" /> <span className="truncate max-w-[12rem]">{file ? file.name : "Attach / replace file"}</span>
                    <input type="file" accept={SEGMENT_ACCEPT} className="hidden"
                        onChange={(e) => { const f = acceptSegmentFile(e.target.files[0]); setFile(f); if (!f) e.target.value = ""; }} />
                </label>
                {segment.hasAttachment && !file && (
                    <>
                        {/* Opens the same in-app reader students get — the file
                            has no public URL to link to any more. */}
                        <button onClick={() => setPreview((p) => !p)} className="text-blue-600 hover:underline text-xs">
                            {preview ? "Hide preview" : "Preview file"}
                        </button>
                        <button onClick={() => save({ removeAttachment: true })} className="text-red-500 text-xs hover:underline">Remove file</button>
                    </>
                )}
                <button onClick={() => save()} disabled={saving} className="ml-auto bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                    <FaSave /> {saving ? "Saving…" : "Save segment"}
                </button>
            </div>

            {preview && segment.media_url && (
                <SecureMedia url={segment.media_url} kind={segment.attachment_kind}
                    documentUrl={segment.document_url} title={segment.title} />
            )}
        </div>
    );
};

// Inline "add a segment" form appended below the segment list.
const AddSegment = ({ subject, topicsBySubject, onAdd }) => {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [topic, setTopic] = useState("");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!title.trim()) { toast.error("Segment title is required"); return; }
        setSaving(true);
        try {
            await onAdd({ title, body, topic, file });
            setTitle(""); setBody(""); setTopic(""); setFile(null); setOpen(false);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 text-gray-500 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2 font-medium">
                <FaPlus /> Add segment
            </button>
        );
    }
    return (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3 border border-blue-200">
            <input placeholder="Segment title" className="border rounded-lg p-2 w-full" value={title} onChange={(e) => setTitle(e.target.value)} />
            <textarea placeholder="Segment text (optional)" rows={3} className="border rounded-lg p-2 w-full text-sm" value={body} onChange={(e) => setBody(e.target.value)} />
            <TopicSelect subject={subject} topicsBySubject={topicsBySubject} value={topic} onChange={setTopic} placeholder="Segment topic (optional)" />
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer border rounded-lg px-3 py-2 w-fit">
                <FaPaperclip className="shrink-0" /> <span className="truncate max-w-[12rem]">{file ? file.name : "Attach file (optional)"}</span>
                <input type="file" accept={SEGMENT_ACCEPT} className="hidden"
                    onChange={(e) => { const f = acceptSegmentFile(e.target.files[0]); setFile(f); if (!f) e.target.value = ""; }} />
            </label>
            <div className="flex items-center gap-3">
                <button onClick={submit} disabled={saving} className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50">{saving ? "Adding…" : "Add segment"}</button>
                <button onClick={() => setOpen(false)} className="text-gray-500 text-sm font-semibold">Cancel</button>
            </div>
        </div>
    );
};

// Hand-write a practice question for this lesson (subject/class/topic are
// inherited from the lesson server-side). MCQ needs four options + the correct
// letter; theory needs just the question + an optional model answer. This is the
// only way to add questions to a video lesson, where AI generation is off.
const EMPTY_QUESTION = {
    question_type: "mcq", level: "Medium", question: "",
    option_a: "", option_b: "", option_c: "", option_d: "",
    correctAnswer: "A", explanation: "",
};

const AddQuestion = ({ onAdd }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_QUESTION);
    const [saving, setSaving] = useState(false);
    const isMcq = form.question_type === "mcq";

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const submit = async () => {
        if (!form.question.trim()) { toast.error("Write the question text"); return; }
        if (isMcq && !["option_a", "option_b", "option_c", "option_d"].every((k) => form[k].trim())) {
            toast.error("A multiple-choice question needs all four options");
            return;
        }
        setSaving(true);
        try {
            await onAdd(form);
            setForm(EMPTY_QUESTION);
            setOpen(false);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!open) {
        return (
            <button onClick={() => setOpen(true)} className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 text-gray-600 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2 font-medium">
                <FaPen size={13} /> Add a question by hand
            </button>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-4 border border-blue-200">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2"><FaPen size={13} /> Write a question</h3>
                <button onClick={() => { setOpen(false); setForm(EMPTY_QUESTION); }} className="text-gray-400 hover:text-gray-600 text-sm font-semibold">Cancel</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm text-gray-600">Type
                    <select className="border rounded-lg p-2 w-full mt-1" value={form.question_type} onChange={(e) => set({ question_type: e.target.value })}>
                        <option value="mcq">Multiple choice</option>
                        <option value="theory">Theory</option>
                    </select>
                </label>
                <label className="text-sm text-gray-600">Difficulty
                    <select className="border rounded-lg p-2 w-full mt-1" value={form.level} onChange={(e) => set({ level: e.target.value })}>
                        <option>Easy</option><option>Medium</option><option>Hard</option>
                    </select>
                </label>
            </div>

            <textarea placeholder="Write the question in plain text (avoid $ or LaTeX — e.g. x^2 not $x^2$)" rows={3}
                className="border rounded-lg p-2 w-full text-sm" value={form.question} onChange={(e) => set({ question: e.target.value })} />

            {isMcq && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {["A", "B", "C", "D"].map((l) => (
                            <input key={l} placeholder={`Option ${l}`} className="border rounded-lg p-2 text-sm"
                                value={form[`option_${l.toLowerCase()}`]} onChange={(e) => set({ [`option_${l.toLowerCase()}`]: e.target.value })} />
                        ))}
                    </div>
                    <label className="text-sm text-gray-600 block">Correct answer
                        <select className="border rounded-lg p-2 w-full mt-1 sm:w-40" value={form.correctAnswer} onChange={(e) => set({ correctAnswer: e.target.value })}>
                            {["A", "B", "C", "D"].map((l) => <option key={l}>{l}</option>)}
                        </select>
                    </label>
                </>
            )}

            <textarea placeholder={isMcq ? "Explanation (optional)" : "Model answer (optional)"} rows={2}
                className="border rounded-lg p-2 w-full text-sm" value={form.explanation} onChange={(e) => set({ explanation: e.target.value })} />

            <button onClick={submit} disabled={saving} className="bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2">
                <FaPlus size={13} /> {saving ? "Adding…" : "Add question"}
            </button>
        </div>
    );
};

export default ContentBuilder;
